import { useState } from 'react';

import { FORM_GAP_TYPES, GAP_TYPE_META } from '../gap-meta';
import { useAppConfig } from '../state/config';
import { createGap, patchGap } from '../state/gaps';
import { useProfile } from '../state/profile';
import type { Gap, GapType } from '../types';
import { GEO_ALL } from '../types';

import { Modal } from './Modal';

/**
 * Modal composer for **form-scoped** gaps
 * (Change form fields / Change workflow·approvals / Other changes).
 * Stream-scoped "New form required" entries go through `NewFormRequiredDialog`.
 *
 * Also handles editing: pass ``existing`` to pre-fill the form and route
 * submit through ``patchGap``. The form/stream/kb identity fields are
 * inherited from the existing gap and aren't editable (the router rejects
 * changing scope/kb/form_sys_id anyway).
 */
export function GapComposer(
  props: {
    onCancel: () => void;
    onSaved: () => void;
  } & (
    | {
        existing: Gap;
        stream?: never;
        kbNumber?: never;
        formSysId?: never;
        formName?: never;
        topBarGeo?: never;
      }
    | {
        existing?: undefined;
        stream: string;
        kbNumber: string;
        formSysId: string | null;
        formName: string | null;
        /** Top-bar selection; used to pre-fill geography unless it's 'all'. */
        topBarGeo: string;
      }
  ),
) {
  const { existing, onCancel, onSaved } = props;
  const cfg = useAppConfig();
  const { profile } = useProfile();
  const isEdit = !!existing;

  const stream = existing?.stream ?? props.stream!;
  const kbNumber = existing?.kb_number ?? props.kbNumber!;
  const formName = existing?.form_name_snapshot ?? props.formName ?? null;
  const fieldName = existing ? existing.field_name : null;

  const firstGeoId = cfg.geographies[0]?.id ?? '';
  const initialGeo =
    existing?.geography ??
    (props.topBarGeo && props.topBarGeo !== GEO_ALL ? props.topBarGeo : firstGeoId);

  const [gapType, setGapType] = useState<GapType>(
    existing ? existing.gap_type : 'change_form_fields',
  );
  const [geography, setGeography] = useState(initialGeo);
  const [description, setDescription] = useState(existing?.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descLen = description.trim().length;
  const descShort = descLen > 0 && descLen < 30;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!geography) return setError('Please pick a geography.');
    if (!description.trim()) return setError('Description is required.');
    setSaving(true);
    try {
      if (isEdit) {
        await patchGap(existing.id, {
          gap_type: gapType,
          geography,
          description: description.trim(),
        });
      } else {
        await createGap({
          scope: 'form',
          stream,
          geography,
          gap_type: gapType,
          description: description.trim(),
          author_name: profile?.full_name ?? 'Unknown',
          author_email: profile?.email ?? null,
          kb_number: kbNumber,
          form_sys_id: props.formSysId ?? null,
          form_name_snapshot: formName,
          proposed_form_name: null,
          field_name: fieldName,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit gap' : 'Log a gap on this form'}
      subtitle={
        <>
          {formName ? (
            <>
              <span className="font-medium text-slate-700">{formName}</span>
              {kbNumber && (
                <span className="text-slate-400"> · {kbNumber}</span>
              )}
            </>
          ) : (
            <span className="font-mono text-slate-500">{kbNumber}</span>
          )}
          {fieldName && (
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5 align-middle">
              field <span className="font-mono normal-case">{fieldName}</span>
            </span>
          )}
        </>
      }
      onClose={saving ? undefined : onCancel}
      size="xl"
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="label">Type</label>
            <select
              className="input h-10"
              value={gapType}
              onChange={(e) => setGapType(e.target.value as GapType)}
            >
              {FORM_GAP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {GAP_TYPE_META[t].label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="label">Geography</label>
            <select
              className="input h-10"
              value={geography}
              onChange={(e) => setGeography(e.target.value)}
            >
              {cfg.geographies.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="label" htmlFor="gap-description">
            Description
          </label>
          <textarea
            id="gap-description"
            autoFocus
            className="input min-h-[160px] text-[0.95rem] leading-relaxed"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`Describe the required change in full, including the business justification — why this change matters, what problem it solves, and for whom.`}
          />
          <p className="text-xs text-slate-500">
            A <span className="font-semibold">full description of the required change with its justification</span>{' '}
            is required. Include who is affected, the process impact, and any
            regulatory or compliance drivers.
          </p>
          {descShort && (
            <p className="text-xs text-amber-700">
              Consider adding more detail so the gap is actionable downstream.
            </p>
          )}
        </div>

        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            className="btn-ghost h-9"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary h-9 px-5" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save gap'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
